import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import commaize from 'js/utils/commaize';

import type { ArmSpec, ArmStamp, OpenContext } from '../armRegistry';
import { isDownloadRead, modalVersion } from '../openerIntent';
import {
  fetchDownloadCount,
  fetchPercentile,
  loswpWorkId,
} from '../workContext';

type ResolvedStats = { percentile: string | null; downloads: number | null };

function qualifyingViews(ctx: OpenContext): number | null {
  if (!isDownloadRead(ctx.detail)) return null;
  const views = ctx.detail.workViewCount;
  return typeof views === 'number' && views > 0 ? views : null;
}

// The Your paper card byline already carries the view count in the paper_card
// header, so the strip claims views only in the default header.
function viewsLeg(ctx: OpenContext): number | null {
  return modalVersion(ctx.detail) === 'default' ? qualifyingViews(ctx) : null;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function countLeg(count: number, noun: string): string {
  return `${commaize(count)} ${noun}${count === 1 ? '' : 's'}`;
}

const LiveStatsStrip: React.FC<{ views: number | null }> = ({ views }) => {
  const [stats, setStats] = useState<ResolvedStats | null>(null);

  useEffect(() => {
    const workId = loswpWorkId();
    if (workId === null) {
      setStats({ percentile: null, downloads: null });
      return undefined;
    }
    let active = true;
    void Promise.all([
      fetchPercentile(workId),
      fetchDownloadCount(workId),
    ]).then(([percentile, downloads]) => {
      if (active) setStats({ percentile, downloads });
    });
    return () => {
      active = false;
    };
  }, []);

  if (stats === null) {
    return (
      <Strip>
        <SkeletonBar aria-hidden="true" />
      </Strip>
    );
  }

  const parts: string[] = [];
  if (stats.percentile) parts.push(capitalize(stats.percentile));
  if (typeof stats.downloads === 'number' && stats.downloads > 0) {
    parts.push(countLeg(stats.downloads, 'download'));
  }
  if (views !== null) parts.push(countLeg(views, 'view'));

  if (parts.length === 0) return null;
  return <Strip>{parts.join(' · ')}</Strip>;
};

export const liveStatsSpec: ArmSpec = {
  slots: (ctx) => {
    if (!isDownloadRead(ctx.detail)) return undefined;
    if (loswpWorkId() === null && viewsLeg(ctx) === null) return undefined;
    return { underHeader: <LiveStatsStrip views={viewsLeg(ctx)} /> };
  },
  stamp: (ctx): ArmStamp => {
    const active =
      isDownloadRead(ctx.detail) &&
      (loswpWorkId() !== null || viewsLeg(ctx) !== null);
    return { render_state: active ? 'rendered' : 'fallback' };
  },
};

const Strip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  margin-top: var(--spacing-xxs-8, 8px);
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--text-primary, #26262b);
  text-align: center;
`;

const SkeletonBar = styled.div`
  width: 140px;
  height: 12px;
  border-radius: var(--border-radius-radius-full, 999px);
  background: var(--background-secondary, #f7f7f8);
  animation: live-stats-pulse 1.2s ease-in-out infinite;

  @keyframes live-stats-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
`;
